import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Home, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }
function Star({ r }) {
  if (r == null) return <span className="text-[10.5px] text-[#A8B0AA]">—</span>;
  return <span className="font-semibold text-[#A06628] tabular-nums">{Number(r).toFixed(1)}<span className="text-[#D4A85C] ml-0.5">★</span></span>;
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#4A7C5E]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
    </div>
  );
}

export default function HomeHealthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [state, setState] = useState(params.get('state') || '');
  const [minQuality, setMinQuality] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 40;

  useEffect(() => { setPage(0); }, [q, state, minQuality, sort]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.homeHealth({
        q: q || undefined,
        state: state || undefined,
        min_quality: minQuality || undefined,
        sort,
        limit: LIMIT,
        offset: page * LIMIT,
      }).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, minQuality, sort, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Home Health' }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E5F0EA] text-[#2D5F4F]">
              <Home size={11} /> Post-acute layer
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">Home Health Agencies</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[640px]">
              Every CMS-certified <strong>home-health agency</strong> in the country. Connect these into the continuum-of-care alongside SNFs &amp; hospitals.
            </p>
          </div>
          <Link to="/hospice" className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="link-hospice">
            View Hospices →
          </Link>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="relative md:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A857F]" />
            <input
              data-testid="hha-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search agency name…"
              className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-9 pr-3 text-[13px] outline-none focus:border-[#4A7C5E]"
            />
          </div>
          <Select testId="hha-filter-state" value={state} onChange={setState} options={US_STATES} placeholder="State" />
          <Select testId="hha-filter-quality" value={minQuality} onChange={setMinQuality} options={[
            { value: '5', label: '5★ only' },
            { value: '4', label: '4★ +' },
            { value: '3', label: '3★ +' },
          ]} placeholder="Min quality" />
        </div>

        <div className="flex items-center gap-2 mb-3 text-[12.5px] text-[#7A857F]">
          <span data-testid="hha-result-count">{fmtInt(data.total)} agencies</span>
          <span className="ml-auto flex items-center gap-2">
            Sort:
            <select data-testid="hha-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#E5DCC4] rounded h-8 px-2 text-[12px]">
              <option value="name">Name</option>
              <option value="rating">Quality ↓</option>
            </select>
          </span>
        </div>

        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Agency</th>
                  <th className="text-left px-4 py-2.5">City / State</th>
                  <th className="text-left px-4 py-2.5">Ownership</th>
                  <th className="text-left px-4 py-2.5">Quality</th>
                  <th className="text-left px-4 py-2.5">Patient survey</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#7A857F]">Loading…</td></tr>}
                {!loading && data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#7A857F]">No agencies match these filters.</td></tr>}
                {!loading && data.items.map((a) => (
                  <tr key={a.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC] cursor-pointer" onClick={() => navigate(`/home-health/${a.ccn}`)} data-testid={`hha-list-${a.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/home-health/${a.ccn}`} className="text-[#1A2622] font-medium hover:text-[#4A7C5E]">{a.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">CCN {a.ccn} · {a.address || ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{a.city || '—'}, {a.state || ''}</td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><div className="truncate max-w-[180px]" title={a.ownership}>{a.ownership || '—'}</div></td>
                    <td className="px-4 py-2.5"><Star r={a.quality_rating} /></td>
                    <td className="px-4 py-2.5"><Star r={a.survey_rating} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] text-[12px]">
            <span className="text-[#7A857F]">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <button data-testid="hha-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Prev</button>
              <button data-testid="hha-next" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span><strong>Source:</strong> {data.source || 'CMS Home Health Care - Provider Data (6jpm-sxkc)'}. Updated monthly by CMS.</span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
